<template>
  <div>
    <popup-hint hint="productsreconcile">
      <template v-slot:breadcrumbs>
        <breadcrumbs :back="back" home="Product reconciliations"/>
      </template>
      <template v-slot:top>
        Reconcile all your product inventory in a single operation.
      </template>
    </popup-hint>

    <purtrak-table name="productreconciliations" objectType="product reconciliations" :defaultColumns="defaultColumns" :headers="headers" ref="table"
                   listEndPoint="productReconciliations2"
                   fields="id,date,user,site{id,name},draft,bonded,bottlesOnly"
                   :listFilters="listFilters"
                   show-expand
                   @itemExpanded="({item, value}) => expandReconciliation(item, value)"
                   @dblclick="reconciliation => $router.push({name:reconciliation.draft?'InventoryProductsReconcileEdit':'InventoryProductsReconcileTransactions',params:{id:reconciliation.id}})"
                   :item-class="reconciliation => (reconciliation.draft ? 'grey--text' : '')"
                   title="Product Reconciliations"
                   :print-info="{subTitle:[filters.from?dateDisplay:'',filters.bonded=='1'?'In-bond':(filters.bonded=='0'?'Duty-paid':''),filters.draft=='1'?'Draft':'',filters.siteId?'Site: ' + siteName : '']}"
                   download-endpoint="productReconciliationsExportCSV">
      <template v-slot:filter>
        <filter-menu include-reset @reset="filters.draft='0';filters.from=null;filters.to=null;siteId=null;bonded='2'">
          <v-list-item>
            <date-range :from.sync="filters.from" :to.sync="filters.to" v-model="dateDisplay" option="All" allow-all/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Type" dense outlined :items="[{value:'2',text:'All'},{divider:true},{value:'1',text:'In-bond'},{value:'0',text:'Duty-paid'}]" v-model="filters.bonded"/>
          </v-list-item>
          <v-list-item>
            <v-checkbox v-model="filters.draft" true-value="1" false-value="0" label="Draft only" class="mt-0"/>
          </v-list-item>
          <v-list-item v-if="sites.length>1">
            <v-autocomplete label="Site" dense outlined :items="[{id:null,name:'All'},{divider:true},...sites]" v-model="filters.siteId" item-text="name" item-value="id"  @change="refresh()"/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.from!=null" close small class="ml-1" @click:close="filters.from=null;filters.to=null">{{dateDisplay}}</v-chip>
          <v-chip v-show="filters.bonded!='2'" close small class="ml-1" @click:close="filters.bonded='2'">{{filters.bonded=='1'?'In-bond':'Duty-paid'}}</v-chip>
          <v-chip v-show="filters.draft=='1'" close small class="ml-1" @click:close="filters.draft='0'">Draft only</v-chip>
          <v-chip v-show="filters.siteId!=null" close small class="ml-1" @click:close="filters.siteId=null;refresh()">{{siteName}}</v-chip>
        </template>
      </template>

      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'InventoryProductsReconcileEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
        <v-icon v-if="!item.draft" @click="expand(!isExpanded)">{{isExpanded?'mdi-chevron-up':'mdi-chevron-down'}}</v-icon>
      </template>
      <template v-slot:item.date="{value}">
        {{value | formatDateTime}}
      </template>
      <template v-slot:item.bonded="{value}">
        {{value ? 'In-bond' : 'Duty-paid'}}
      </template>
      <template v-slot:item.draft="{value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon v-else-if="value">mdi-check</v-icon>
      </template>
      <template v-slot:expanded-item="{ item, headers }">
        <td :colspan="headers.length">
          <v-card class="my-4 text-caption" :class="expansionPanelClass">
            <v-simple-table dense class="my-2" :class="expansionPanelClass" style="width:100%">
              <thead>
                <tr>
                  <th :class="expansionPanelClass" width="27%">Product</th>
                  <th :class="expansionPanelClass" width="27%">Action</th>
                  <th v-if="lotTracking(item)" :class="expansionPanelClass" width="15%">Lot</th>
                  <th class="text-right" :class="expansionPanelClass" width="15%">Adjustment</th>
                  <th class="text-right" :class="expansionPanelClass" width="15%">Value</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="transaction of item.transactions" :key="transaction.id">
                  <td class="text-caption">{{transaction.name}}</td>
                  <td class="text-caption">
                    <a @click="$router.push({name:transaction.type==='ProductTransactionReduceInventory'?'InventoryProductsReduceInventory':'InventoryProductsAdjustInventory',params:{transactionId:transaction.id}})">
                      {{transaction.description}}
                    </a>
                  </td>
                  <td v-if="lotTracking(item)" class="text-caption">
                    <a @click.stop="$router.push({name:'ReportsView',params:{back:[...back,{text:'Product reconciliations',to:{name:'InventoryProductsReconcile'}}]},query:{to:'reports/inventory/ReportsLot',id:transaction.id,lot:transaction.lot,forward:0}})">
                      {{transaction.lot}}
                    </a>
                  </td>
                  <td class="text-caption text-right">
                    <template v-if="item.bottlesOnly">{{transaction.bottles | number(0)}} bottles</template>
                    <template v-else>{{transaction.cases | number(1)}} cases</template>
                  </td>
                  <td class="text-caption text-right">{{transaction.value | currency}}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </td>
      </template>
      <template v-slot:item.action="{item}">
        <v-menu :nudge-width="150" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn small v-on="on" v-if="$vuetify.breakpoint.mobile" outlined>Reconciliation operations</v-btn>
            <v-btn small icon v-on="on" v-else><v-icon small>mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <v-list-item :to="{name:item.draft?'InventoryProductsReconcileEdit':'InventoryProductsReconcileTransactions',params:{id:item.id}}"><v-list-item-title>{{item.draft?'Edit':'View transactions'}}</v-list-item-title></v-list-item>
            <v-list-item v-if="audit" :to="{name:'Audit',query:{entityId:item.id}}"><v-list-item-title>Audit history</v-list-item-title></v-list-item>
            <template v-if="!readOnly">
              <v-divider/>
              <v-list-item @click="reconciliation=item;showDelete=true"><v-list-item-title>Delete reconciliation</v-list-item-title></v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>
    </purtrak-table>

    <delete-object v-model="showDelete" name="this reconciliation" @delete="performDeleteTransaction"/>
  </div>
</template>

<script>

export default {
  name: 'InventoryProductsReconcile',
  data() {
    return {
      filters: {
        from: null,
        to: null,
        draft: '0',
        siteId: null,
        bonded: '2'
      },
      dateDisplay: '',
      reconciliation: null,
      showDelete: false,
      defaultColumns: [{ value: 'date', visible:true},
                       { value: 'bonded', visible:true},
                       { value: 'user', visible:true},
                       { value: 'site.name', visible:true},
                       { value: 'draft', visible:true}],
      headers: [{ text: 'DATE', value: 'date', width:'20%',sortable:true},
                { text: 'TYPE', value: 'bonded', width:'10%',align:'center',sortable:true},
                { text: 'USER', value: 'user', width:'30%',sortable:true},
                { text: 'SITE', value: 'site.name', width:'30%',sortable:true, omit:() => this.sites.length <= 1},
                { text: 'DRAFT', value: 'draft', width:'10%',sortable:true,align:'center'}]
    }
  },
  computed: {
    back() {
      return [{text:'Products',to:{name:'InventoryProducts',query:{siteId:this.filters.siteId}}}];
    },
    sites() {
      return this.$store.getters.sites();
    },
    siteName() {
      return this.sites.find(site => site.id === this.filters.siteId)?.name;
    },
    company() {
      return this.$store.getters.company;
    },
    readOnly() {
      return !this.company.permissions.includes('inventory.write');
    },
    audit() {
      return this.company.allowAudit && this.company.permissions.includes('audit');
    },
    listFilters() {
      const filters = [];
      if (this.filters.from)
        filters.push({field:'date', comparator:'ge', value:`${this.filters.from} 00:00:00`});
      if (this.filters.to)
        filters.push({field:'date', comparator:'le', value:`${this.filters.to} 23:59:59`});
      if (this.filters.siteId !== null)
        filters.push({field:'siteId', comparator:'eq', value:this.filters.siteId});
      if (this.filters.draft === '1')
        filters.push({field:'draft', comparator:'eq', value:'true'});
      if (this.filters.bonded !== '2')
        filters.push({field:'bonded', comparator:'eq', value:this.filters.bonded === '1' ? 'true' : 'false'});

      return filters;
    }
  },
  methods: {
    lotTracking(item) {
      return this.company.settings.lotTrackingForProducts && (item.bonded || this.company.settings.lotTrackingForDutyPaid);
    },
    async performDeleteTransaction() {
      this.loading = true;
      const results = await this.$apiCall(`mutation InventoryProductsReconcile($id: ID!) {
                                            objectDelete(id:$id)
                                          }`,
                                          {id:this.reconciliation.id});
      this.loading = false;
      if (!results.error) {
        this.showDelete = false;
        this.$refs.table.buildList();
        this.$store.commit('setError', {type:'success',message:`Deleted reconciliation`});
      }
    },
    async expandReconciliation(item, value) {
      if (value && !item.transactions) {
        this.loading = true;
        const results = await this.$apiCall(`query InventoryProductsReconcile($id:ID!) {
                                                  productReconciliation(id:$id) {
                                                    transactions {id,type,name,description,bottles,cases,value,lot}
                                                  }
                                                }`,
                                            {id:item.id});
        this.$set(item, 'transactions', results.data.productReconciliation.transactions);
        this.loading = false;
      }
    }
  }
}
</script>